"use client";

// src/patterns/navigation/index.tsx
import React from "react";
import { Tabs } from "radix-ui";
import { AccessibleIcon } from "radix-ui";
import { motion } from "motion/react";
import { nav } from "@pinatagrams/design-system/recipes";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var Navigation = ({
  value,
  onValueChange,
  label = "Bottom navigation menu",
  children
}) => {
  const classes = nav();
  return /* @__PURE__ */ jsx(Tabs.Root, { value, onValueChange, orientation: "vertical", asChild: true, children: /* @__PURE__ */ jsx(
    motion.nav,
    {
      initial: { y: 100, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: 100, opacity: 0 },
      transition: { type: "spring", stiffness: 300, damping: 30 },
      role: "navigation",
      "aria-label": label,
      className: classes.root,
      children
    }
  ) });
};
var NavigationTitle = ({ children }) => {
  const classes = nav();
  return /* @__PURE__ */ jsx("div", { className: classes.title, children });
};
var NavigationList = ({ children }) => {
  const classes = nav();
  return /* @__PURE__ */ jsx(Tabs.List, { "aria-label": "Tabs", asChild: true, children: /* @__PURE__ */ jsx(motion.ul, { className: classes.list, children }) });
};
var NavigationTab = ({
  value,
  icon,
  disabled = false,
  variant = "default",
  children,
  onClick,
  asChild = false
}) => {
  const [isActive, setIsActive] = React.useState(false);
  const buttonRef = React.useRef(null);
  const setButtonRef = React.useCallback((node) => {
    buttonRef.current = node;
  }, []);
  const motionVariants = {
    initial: {
      opacity: 0.6
    },
    show: {
      opacity: 1
    }
  };
  React.useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;
    const observer = new MutationObserver(() => {
      const active = button.getAttribute("data-state") === "active";
      const isDisabled = button instanceof HTMLButtonElement ? button.disabled : button.getAttribute("aria-disabled") === "true";
      console.log(`[NavigationTab:${value}] data-state changed:`, {
        active,
        dataState: button.getAttribute("data-state"),
        disabled: isDisabled
      });
      setIsActive(active);
    });
    observer.observe(button, { attributes: true, attributeFilter: ["data-state"] });
    setIsActive(button.getAttribute("data-state") === "active");
    return () => observer.disconnect();
  }, [value]);
  const handleClick = (e) => {
    console.log(`[NavigationTab:${value}] clicked:`, {
      disabled,
      isActive,
      variant,
      defaultPrevented: e.defaultPrevented
    });
    if (onClick && isActive && !disabled) {
      console.log(`[NavigationTab:${value}] Triggering custom onClick handler`);
      onClick(value);
    }
  };
  const content = asChild ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
    icon && /* @__PURE__ */ jsx(AccessibleIcon.Root, { label: typeof children === "string" ? children : "Navigation item", children: icon }),
    /* @__PURE__ */ jsx("span", { children })
  ] });
  const classes = nav();
  return /* @__PURE__ */ jsx(
    motion.li,
    {
      className: classes.listItem,
      variants: motionVariants,
      initial: "initial",
      whileHover: disabled ? void 0 : "show",
      whileTap: disabled ? void 0 : "show",
      animate: isActive ? "show" : "initial",
      children: /* @__PURE__ */ jsx(Tabs.Trigger, { value, disabled, asChild: true, children: asChild ? React.cloneElement(children, {
        ref: setButtonRef,
        className: [
          classes.link || "",
          children.props?.className || ""
        ].filter(Boolean).join(" "),
        "data-icon": !!icon,
        "data-variant": variant,
        onClick: handleClick,
        "aria-disabled": disabled
      }) : /* @__PURE__ */ jsx(
        motion.button,
        {
          ref: setButtonRef,
          className: classes.link,
          "data-icon": !!icon,
          "data-variant": variant,
          onClick: handleClick,
          children: content
        }
      ) })
    }
  );
};
var NavigationFooter = ({ children }) => {
  const classes = nav();
  return /* @__PURE__ */ jsx("footer", { className: classes.footer, children });
};
Navigation.Title = NavigationTitle;
Navigation.List = NavigationList;
Navigation.Tab = NavigationTab;
Navigation.Footer = NavigationFooter;
var navigation_default = Navigation;
export {
  Navigation,
  navigation_default as default
};
//# sourceMappingURL=navigation.js.map